/* eslint-disable react/react-in-jsx-scope */
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import ErrorBoundary from "@/components/errors/ErrorBoundary";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { cn } from "@/libs/utils";
import {
  Building2,
  ExternalLink,
  Mail,
  Phone,
  ShieldCheck,
  User,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

type MediaLight = { id: number; name?: string; uri?: string };

type Representant = {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
};

type FunderApi = {
  id: number;
  structureName?: string;
  fullname?: string; // parfois
  siret?: string;
  nif?: string;
  photo?: MediaLight | null;
  representants?: Representant[];
  email?: string;
  phone?: string;
  website?: string;
};

function pickFunderName(f: any) {
  return f?.structureName || f?.fullname || `Financeur #${f?.id ?? "?"}`;
}

function safeArray<T = any>(v: any): T[] {
  return Array.isArray(v) ? v : [];
}

function joinName(r: Representant) {
  const fn = (r?.firstname || "").trim();
  const ln = (r?.lastname || "").trim();
  const full = `${fn} ${ln}`.trim();
  return full || "Représentant";
}

export default function DashboardFunder() {
  const { id } = useParams<{ id: string }>();
  const idFunder = useMemo(() => {
    const n = Number(id);
    return !n || Number.isNaN(n) ? null : n;
  }, [id]);

  const { t } = useTranslation("dashboard", { keyPrefix: "financeur" });
  const { t: tDashboard } = useTranslation("dashboard");

  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const [tab, setTab] = useState<"descriptif" | "financement">(
    tabParam === "financement" ? "financement" : "descriptif"
  );
  const navigate = useNavigate();

  const handleChangeTab = (value: "descriptif" | "financement") => {
    if (value === "financement") {
      navigate(buildSynthesesFinanceurHref);
      return;
    }

    setTab(value);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", value);
        return p;
      },
      { preventScrollReset: true }
    );
  };

  // ===== Fetch financeur =====
  const FUNDER_URL =
    idFunder != null
      ? `${import.meta.env.VITE_API_ENDPOINT}/user/${idFunder}`
      : "";

  const { data: funderData, loading: loadingFunder } = useFetchDataV2<
    FunderApi | any
  >(FUNDER_URL, `funder-${idFunder ?? "na"}`, 1000 * 60 * 60);

  const funder: FunderApi | null = useMemo(() => {
    const raw = (funderData as any)?.data ?? funderData;
    if (!raw || typeof raw !== "object") return null;
    return raw as FunderApi;
  }, [funderData]);

  const buildSynthesesFinanceurHref = useMemo(() => {
    const params = new URLSearchParams();
    if (idFunder != null) {
      params.set("targetFinanceurId", String(idFunder));
    }

    const financeurName = pickFunderName(funder);
    if (financeurName) {
      params.set("targetFinanceurName", financeurName);
    }

    const search = params.toString();
    return search
      ? `/dashboard/syntheses/financeurs?${search}`
      : "/dashboard/syntheses/financeurs";
  }, [funder, idFunder]);

  useEffect(() => {
    const v = searchParams.get("tab");
    if (v === "financement") {
      navigate(buildSynthesesFinanceurHref, { replace: true });
      return;
    }

    if (v === "descriptif") setTab(v);
  }, [buildSynthesesFinanceurHref, navigate, searchParams]);

  if (!idFunder) {
    return (
      <div className="rounded-xl border bg-white p-4 text-sm text-muted-foreground">
        Financeur introuvable (id manquant).
      </div>
    );
  }

  if (loadingFunder) {
    return <FunderSkeleton />;
  }

  const title = pickFunderName(funder);

  return (
    <div className="space-y-4">
      <ReactBreadcrumb
        items={[
          { href: "", name: tDashboard("Mes financeurs") },
          { href: "", name: title },
        ]}
        isTruncated={false}
      />

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[24px] font-medium truncate">{title}</h1>
          <div className="mt-1 text-sm text-muted-foreground">
            {tDashboard("Détail du financeur")}
          </div>
        </div>

        <a
          href={`/dashboard/financeurs`} // ✅ adapte si tu as une route dédiée
          className={cn(
            "inline-flex items-center gap-2 text-sm",
            "rounded-full border bg-white px-3 py-1",
            "hover:bg-slate-50"
          )}
        >
          <ExternalLink className="w-4 h-4" />
          {tDashboard("Retour liste")}
        </a>
      </div>

      <Tabs value={tab} onValueChange={handleChangeTab} className="w-full">
        <TabsList className="mb-2 grid w-full grid-cols-2 bg-background">
          <TabsTrigger
            value="descriptif"
            className="rounded-none border-b-2 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:shadow-none"
          >
            {tDashboard("Descriptifs")}
          </TabsTrigger>
          <TabsTrigger
            value="financement"
            className="rounded-none border-b-2 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:shadow-none"
          >
            {tDashboard("Suivi de financement")}
          </TabsTrigger>
        </TabsList>

        {/* ====== ONGLET: DESCRIPTIF ====== */}
        <TabsContent value="descriptif" className="mb-4">
          <ErrorBoundary>
            <FunderDescriptif funder={funder} />
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ===================== Descriptif ===================== */

function FunderDescriptif({ funder }: { funder: FunderApi | null }) {
  const name = pickFunderName(funder);
  const photoUrl = funder?.photo?.uri
    ? import.meta.env.VITE_IP_API + funder.photo.uri
    : null;

  const reps = safeArray<Representant>(funder?.representants);

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-1">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full border bg-white overflow-hidden">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="photo financeur"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full grid place-items-center text-xs text-muted-foreground">
                  <User className="w-6 h-6" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-base font-semibold truncate">{name}</div>
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" />
                {funder?.siret ? `SIRET: ${funder.siret}` : "SIRET: —"}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-2 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">NIF/STAT</span>
            <span className="font-medium">{funder?.nif || "—"}</span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Email</span>
            <span className="font-medium inline-flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {funder?.email || "—"}
            </span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Téléphone</span>
            <span className="font-medium inline-flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {funder?.phone || "—"}
            </span>
          </div>

          {funder?.website ? (
            <a
              href={funder.website}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 text-sm text-primary underline"
            >
              <ExternalLink className="w-4 h-4" />
              Site web
            </a>
          ) : null}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="pb-2">
          <div className="text-sm font-semibold inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Représentants
          </div>
        </CardHeader>

        <CardContent className="pt-2">
          {reps.length === 0 ? (
            <div className="rounded-lg border bg-white p-3 text-sm text-muted-foreground">
              Aucun représentant.
            </div>
          ) : (
            <div className="space-y-2">
              {reps.map((r) => (
                <div
                  key={r.id}
                  className="rounded-xl border bg-white p-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {joinName(r)}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground space-y-1">
                      <div className="inline-flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5" />
                        {r.email || "—"}
                      </div>
                      <div className="inline-flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5" />
                        {r.phone || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 text-xs text-muted-foreground">
                    #{r.id}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ===================== Skeleton ===================== */

function FunderSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-4 w-48" />
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2 w-full">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-4 w-52" />
        </div>
        <Skeleton className="h-8 w-32 rounded-full" />
      </div>

      <div className="grid w-full grid-cols-2 gap-2">
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-[220px]" />
        <Skeleton className="h-[220px] lg:col-span-2" />
      </div>
    </div>
  );
}
